import React, { useState, useEffect, useContext, useRef } from 'react';
import { Modal, Form, Input, Select, Checkbox, Collapse, Button, Space, Typography, Alert, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../App';
import HelpdeskFormQuestions, { answersToPayload } from './HelpdeskFormQuestions';
import { ticketKey } from './HelpdeskInvolvedTickets';

// parentTicket ({ ID, Subject, AgentUserID, ApplicableUserID }): create a
// follow-up linked to it. Its employee is filled in when the picked Request
// Type allows them.
export default function HelpdeskCreateModal({ open, onClose, filterOpts, parentTicket = null }) {
  const [form]    = Form.useForm();
  const navigate  = useNavigate();
  const currentUser = useContext(UserContext);

  const [saving,    setSaving]    = useState(false);
  const pickedFormId = Form.useWatch('form_id', form);
  // The picked Request Type's questions (GET /api/eval-forms/:id).
  const [formDef,   setFormDef]   = useState(null);
  const [hdForms,   setHdForms]   = useState([]);
  // Who this user may raise a ticket for (an Edit Manager: their teams;
  // anyone else: themselves), and -- for a Request Type with submitters
  // listed -- everyone, as its Applicable Employee.
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [users,     setUsers]     = useState([]);
  const [teams,     setTeams]     = useState([]);
  // Same as the absence create popup: the Team field is filled from the
  // impacted employee and locked, unless the employee has no team.
  const [employeeSelected, setEmployeeSelected] = useState(false);
  const [employeeHasTeam,  setEmployeeHasTeam]  = useState(false);
  // Termed employees still need tickets (an offboard shipment return).
  const [includeInactive,  setIncludeInactive]  = useState(false);
  // Client Ticket Type: the clients this user may pick, each with its
  // campaigns and contacts (GET /api/helpdesk/client-options).
  const [clientChoices, setClientChoices] = useState([]);
  // Self-service: the Request Type's solutions, offered before the ticket
  // form ('solutions' step) and skippable; which were opened is noted on
  // the ticket if one is still created.
  const [solutions, setSolutions] = useState([]);
  const [step, setStep]           = useState('form');   // solutions | form
  const [openedIds, setOpenedIds] = useState([]);
  const pickedClientId = Form.useWatch('client_id', form);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setFormDef(null);
      setSolutions([]);
      setStep('form');
      setOpenedIds([]);
      setEmployeeSelected(false);
      setEmployeeHasTeam(false);
      // A follow-up is often for someone since termed, so it starts on.
      setIncludeInactive(!!parentTicket);
      api.get('/api/helpdesk/forms')
        .then((r) => setHdForms(r.data))
        .catch(() => {});
      api.get('/api/teams')
        .then((r) => setTeams(r.data || []))
        .catch(() => {});
      api.get('/api/helpdesk/filter-options')
        .then((r) => setUsers(r.data.users || []))
        .catch((err) => {
          console.error('helpdesk filter-options failed:', err);
        });
    }
  }, [open, form, currentUser]); // eslint-disable-line

  // The impacted employee choices, with or without inactive employees.
  useEffect(() => {
    if (!open) return;
    api.get('/api/helpdesk/filter-options', { params: includeInactive ? { include_inactive: 1 } : {} })
      .then((r) => { setEmployees(r.data.employees || []); setAllEmployees(r.data.all_employees || []); })
      .catch(() => setEmployees([]));
  }, [open, includeInactive]);

  const formOptions = hdForms.map((f) => ({
    value: f.ID,
    label: f.QueueName ? `${f.Name} (${f.QueueName})` : f.Name,
  }));

  // Restricted Impacted Employees (Submitters) are the only Impacted Employee
  // choices when set (HR picks themselves). Otherwise a Request Type with a
  // Can Create list lets its creators pick anyone (IT, a Shipping Return).
  const pickedForm   = hdForms.find((f) => f.ID === pickedFormId);
  // Applicable Employee Request Types (offboarding): the ticket is about the
  // employee picked here, who never sees it; the Impacted Employee is whoever
  // creates it, set by the server.
  const usesApplicable = !!pickedForm?.UsesApplicableEmployee;
  // A client's own login raises tickets about themselves: no Impacted /
  // Applicable Employee, Team or Watchers to pick (routes/helpdesk sets them).
  const isClient = currentUser?.Role === 'client';
  const isClientTicket = !!pickedForm?.IsClientTicket;
  const pickedClient   = clientChoices.find((c) => c.ID === pickedClientId);

  // The queue's after-hours message while it is closed. The ticket can still
  // be submitted; this only warns. Re-checked when the Request Type or, on a
  // Client Ticket Type, the client changes (the client's queue may differ).
  const [afterHours, setAfterHours] = useState(null);
  useEffect(() => {
    if (!open || !pickedFormId) { setAfterHours(null); return; }
    api.get('/api/helpdesk/queue-hours-check', { params: { form_id: pickedFormId, client_id: pickedClientId || undefined } })
      .then((r) => setAfterHours(r.data?.closed && r.data?.message ? r.data.message : null))
      .catch(() => setAfterHours(null));
  }, [open, pickedFormId, pickedClientId]);

  useEffect(() => {
    if (!open || !isClientTicket) return;
    api.get('/api/helpdesk/client-options')
      .then((r) => {
        const list = r.data || [];
        setClientChoices(list);
        // A client's own login: its one client, picked for it.
        if (isClient && list.length === 1 && !form.getFieldValue('client_id')) {
          form.setFieldsValue({ client_id: list[0].ID, campaign_id: list[0].Campaigns?.[0]?.ID });
        }
      })
      .catch(() => setClientChoices([]));
  }, [open, isClientTicket]); // eslint-disable-line react-hooks/exhaustive-deps
  // Client logins (Role 'client') only on a Client Ticket Type.
  const poolFor = (f, appl) => {
    const keep = (list) => (f?.IsClientTicket ? list : list.filter((u) => u.Role !== 'client'));
    if (appl) return keep((f?.Submitters?.length || f?.HasCreators) ? allEmployees : employees);
    if (f?.Submitters?.length) return f.Submitters;
    return keep(f?.HasCreators ? allEmployees : employees);
  };
  const applicablePool = poolFor(pickedForm, true);
  const employeePool = poolFor(pickedForm, false);
  // Include inactive only means something to someone who can pick others
  // (not when the only choice is themselves).
  const othersIn = (pool) => pool.some((u) => u.ID !== currentUser?.ID);

  // Built in for Offboarding and Onboarding (by name): the subject names the
  // person -- "Offboard <Applicable Employee>", "Onboard <Name of New
  // Employee>" -- filled in as they are picked or typed, and greyed out so it
  // is always exactly that.
  const applicableId = Form.useWatch('applicable_user_id', form);
  const answersNow   = Form.useWatch('answers', form);
  const autoSubject  = useRef('');
  const pickedName   = (pickedForm?.Name || '').trim().toLowerCase();
  const subjectKind  = pickedName.startsWith('offboard') ? 'Offboard' : pickedName.startsWith('onboard') ? 'Onboard' : null;
  let subjectWho = '';
  if (subjectKind === 'Offboard') {
    subjectWho = applicablePool.find((u) => u.ID === applicableId)?.DisplayName || '';
  } else if (subjectKind === 'Onboard') {
    const q = (formDef?.Sections || []).flatMap((sec) => sec.Questions || [])
      .find((x) => /name of new employee/i.test(x.QuestionText || ''));
    subjectWho = q ? String(answersNow?.[String(q.ID)] || '').trim() : '';
  }
  const subjectAuto = subjectKind && subjectWho ? `${subjectKind} ${subjectWho}` : '';
  useEffect(() => {
    if (!open) return;
    // Generated: always the generated text. Otherwise only a subject that was
    // generated is cleared (switching away from Offboarding / Onboarding).
    const now = form.getFieldValue('subject') || '';
    if (subjectKind || now === autoSubject.current) form.setFieldValue('subject', subjectAuto);
    autoSubject.current = subjectAuto;
  }, [open, subjectKind, subjectAuto, form]);
  const employeeOptions = employeePool.map((u) => ({
    value: u.ID,
    label: (u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName)
      + (u.IsActive === 0 || u.IsActive === false ? ' (inactive)' : ''),
  }));

  // Anyone can be a watcher, on any Request Type -- client logins only on a
  // Client Ticket Type, other View Only users never.
  const watcherOptions = users.filter((u) => (u.Role === 'client' ? !!pickedForm?.IsClientTicket : !u.IsViewOnly)).map((u) => ({
    value: u.ID,
    label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName,
  }));

  // Staff picking a Client Contact with a Harbor login: a checkbox (ticked
  // to start) adds that login as a watcher, so the client can follow it.
  const pickedContactId = Form.useWatch('client_contact_id', form);
  const contactLogin = isClientTicket && !isClient
    ? (pickedClient?.Contacts || []).find((k) => k.ID === pickedContactId && k.LoginID && k.LoginID !== currentUser?.ID)
    : null;

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const payload = {
        subject:       values.subject,
        description:   values.description || null,
        form_id:       values.form_id || null,
        agent_user_id: usesApplicable ? null : (values.agent_user_id || null),
        ...(usesApplicable ? { applicable_user_id: values.applicable_user_id || null } : {}),
        team_id:       values.team_id || null,
        watcher_user_ids: [...new Set([...(values.watcher_user_ids || []),
          ...(contactLogin && values.watch_contact !== false ? [contactLogin.LoginID] : [])])],
        answers:       formDef ? answersToPayload(formDef, values.answers) : [],
        ...(solutions.length ? { self_service: { shown_ids: solutions.map((x) => x.ID), opened_ids: openedIds } } : {}),
        ...(isClientTicket ? {
          client_id:         values.client_id,
          client_contact_id: values.client_contact_id || null,
          campaign_id:       values.campaign_id || null,
        } : {}),
        ...(parentTicket ? { parent_ticket_id: parentTicket.ID } : {}),
      };
      const res = await api.post('/api/helpdesk', payload);
      navigate(`/helpdesk/${res.data.id}`);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maskClosable={false}
      title={parentTicket ? `New Follow-up Ticket for ${ticketKey(parentTicket)}` : 'New Help Desk Ticket'}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Create Ticket"
      okButtonProps={{ style: step === 'solutions' && pickedFormId ? { display: 'none' } : undefined }}
      confirmLoading={saving}
      width={560}
      destroyOnClose
    >
      {parentTicket && (
        <div style={{ marginTop: 8, fontSize: 13, color: 'rgba(0,0,0,0.55)' }}>
          Follows {ticketKey(parentTicket)}: {parentTicket.Subject}
        </div>
      )}
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        <Form.Item name="form_id" label="Request Type" rules={[{ required: true, message: 'Request Type is required' }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select help desk form"
            options={formOptions}
            onChange={(id) => {
              form.setFieldValue('answers', {});
              setFormDef(null);
              // The employee choices depend on the form, so start the pick again
              // -- with the original ticket's employee on a follow-up, when
              // this Request Type lets them be picked.
              const f = hdForms.find((x) => x.ID === id);
              const appl = !!f?.UsesApplicableEmployee;
              const pool = poolFor(f, appl);
              const parentEmp = parentTicket ? (parentTicket.ApplicableUserID || parentTicket.AgentUserID) : null;
              // The original ticket's employee on a follow-up; otherwise whoever
              // is creating it, when they are one of the choices (most tickets
              // are for oneself; another employee can still be picked) -- but
              // not as the Applicable Employee, who is someone else (an
              // offboard), except on a Client Ticket Type; otherwise, when
              // there is only one choice, that one.
              const keep = (parentEmp ? pool.find((u) => u.ID === parentEmp) : null)
                || (!appl || f?.IsClientTicket ? pool.find((u) => u.ID === currentUser?.ID) : null)
                || (pool.length === 1 ? pool[0] : null);
              form.setFieldsValue({
                client_id: undefined,
                client_contact_id: undefined,
                campaign_id: undefined,
                agent_user_id: appl ? undefined : keep?.ID,
                applicable_user_id: appl ? keep?.ID : undefined,
                team_id: keep?.TeamID || undefined,
                watcher_user_ids: [],
              });
              setEmployeeSelected(!!keep);
              setEmployeeHasTeam(!!keep?.TeamID);
              if (id) api.get(`/api/eval-forms/${id}`).then((r) => setFormDef(r.data)).catch(() => {});
              setSolutions([]);
              setOpenedIds([]);
              setStep('form');
              if (id) {
                api.get(`/api/helpdesk/forms/${id}/solutions`)
                  .then((r) => {
                    const list = r.data || [];
                    setSolutions(list);
                    setStep(list.length ? 'solutions' : 'form');
                  })
                  .catch(() => {});
              }
            }}
          />
        </Form.Item>

        {afterHours && (
          <Alert type="warning" showIcon style={{ marginBottom: 12, whiteSpace: 'pre-wrap' }} message={afterHours} />
        )}

        {/* Self-service first, when the Request Type has solutions. */}
        {pickedFormId && step === 'solutions' && (
          <div>
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message="Try these first. If one fixes it, no ticket is needed." />
            <Collapse
              size="small"
              onChange={(keys) => setOpenedIds((prev) => [...new Set([...prev, ...[].concat(keys).map(Number)])])}
              items={solutions.map((sol) => ({
                key: String(sol.ID),
                label: sol.Title,
                children: (
                  <div>
                    {sol.Steps && (
                      <ol style={{ paddingLeft: 20, margin: '0 0 8px' }}>
                        {sol.Steps.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}
                      </ol>
                    )}
                    {sol.URL && (
                      <div style={{ marginBottom: 8 }}>
                        <Typography.Link href={sol.URL} target="_blank" rel="noopener noreferrer">{sol.URL}</Typography.Link>
                      </div>
                    )}
                    <Button type="primary" size="small" onClick={async () => {
                      try {
                        await api.post('/api/helpdesk/self-service/solved', {
                          form_id: pickedFormId, solution_id: sol.ID, shown_ids: solutions.map((x) => x.ID),
                        });
                      } catch { /* the fix still counts for the user */ }
                      message.success('Glad that fixed it. No ticket was created.');
                      onClose();
                    }}>
                      That fixed it
                    </Button>
                  </div>
                ),
              }))}
            />
            <Space style={{ marginTop: 12 }}>
              <Button onClick={() => setStep('form')}>I still need help</Button>
            </Space>
          </div>
        )}

        {/* Everything else once a Request Type is picked: its questions
            depend on it, so it comes first. */}
        {pickedFormId && step === 'form' && (
          <>
          <Form.Item name="subject" label="Subject"
                     rules={[{ required: true, message: subjectKind === 'Offboard' ? 'Pick the Applicable Employee to fill in the subject'
                       : subjectKind === 'Onboard' ? 'Fill in Name of New Employee to fill in the subject' : 'Subject is required' }]}>
            <Input
              disabled={!!subjectKind}
              placeholder={subjectKind === 'Offboard' ? 'Filled in from the Applicable Employee'
                : subjectKind === 'Onboard' ? 'Filled in from Name of New Employee' : 'Brief description of the issue'}
            />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={4} placeholder="Describe the issue in detail" />
          </Form.Item>


          {isClientTicket && (
            <>
              <Form.Item name="client_id" label="Client" rules={[{ required: true, message: 'Client is required' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select client"
                  options={clientChoices.map((c) => ({ value: c.ID, label: c.Name }))}
                  onChange={(id) => {
                    const cl = clientChoices.find((c) => c.ID === id);
                    form.setFieldsValue({
                      client_contact_id: undefined,
                      campaign_id: cl?.Campaigns?.[0]?.ID,
                    });
                  }}
                />
              </Form.Item>
              {pickedClient?.Campaigns?.length > 0 && (
                <Form.Item name="campaign_id" label="Campaign" rules={[{ required: true, message: 'Campaign is required' }]}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={pickedClient.Campaigns.map((cp) => ({ value: cp.ID, label: cp.Name }))}
                  />
                </Form.Item>
              )}
              {/* A client's own login is always its own contact (the server
                  sets it). */}
              {!isClient && (
              <Form.Item name="client_contact_id" label="Client Contact">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder="Select contact"
                  disabled={!pickedClient}
                  options={(pickedClient?.Contacts || []).map((k) => ({
                    value: k.ID, label: k.Title ? `${k.Name} (${k.Title})` : k.Name,
                  }))}
                />
              </Form.Item>
              )}
            </>
          )}

          <HelpdeskFormQuestions formDef={formDef} />

          {/* A Client Ticket Type has no Impacted / Applicable Employee or
              Team: it is about the Client and Contact (the server stores
              whoever raises it). */}
          {usesApplicable && !isClient && !isClientTicket && (
            <Form.Item name="applicable_user_id"
              label={
                <span>
                  Applicable Employee
                  {othersIn(applicablePool) && (
                  <Checkbox
                    style={{ marginLeft: 12, fontWeight: 'normal' }}
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                  >
                    Include inactive
                  </Checkbox>
                  )}
                </span>
              }
              rules={[{ required: true, message: 'Applicable Employee is required' }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select employee"
                disabled={!othersIn(applicablePool)}
                options={applicablePool.map((u) => ({
                  value: u.ID,
                  label: (u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName)
                    + (u.IsActive === 0 || u.IsActive === false ? ' (inactive)' : ''),
                }))}
                allowClear
                filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
                onChange={(id) => {
                  setEmployeeSelected(!!id);
                  const emp = id ? applicablePool.find((u) => u.ID === id) : null;
                  form.setFieldValue('team_id', emp?.TeamID || undefined);
                  setEmployeeHasTeam(!!emp?.TeamID);
                }}
              />
            </Form.Item>
          )}
          {!usesApplicable && !isClient && !isClientTicket && (
          <Form.Item name="agent_user_id"
            label={
              <span>
                Impacted Employee
                {/* Restricted Impacted Employees are picked from that list instead. */}
                {!pickedForm?.Submitters?.length && othersIn(employeePool) && (
                  <Checkbox
                    style={{ marginLeft: 12, fontWeight: 'normal' }}
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                  >
                    Include inactive
                  </Checkbox>
                )}
              </span>
            }
            rules={[{ required: true, message: 'Impacted Employee is required' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select employee"
              // Read only when the only choice is yourself.
              disabled={!othersIn(employeePool)}
              options={employeeOptions}
              allowClear
              filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
              onChange={(id) => {
                setEmployeeSelected(!!id);
                const emp = id ? employeePool.find((u) => u.ID === id) : null;
                form.setFieldValue('team_id', emp?.TeamID || undefined);
                setEmployeeHasTeam(!!emp?.TeamID);
              }}
            />
          </Form.Item>
          )}

          {!isClient && (<>
          {!isClientTicket && (
          <Form.Item name="team_id" label="Team" rules={[{ required: true, message: 'Team is required' }]}>
            <Select
              showSearch
              placeholder="Select team"
              options={teams.map((t) => ({ value: t.ID, label: t.Name }))}
              disabled={!employeeSelected || employeeHasTeam}
              filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          )}

          <Form.Item name="watcher_user_ids" label="Watchers">
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Add watchers"
              options={watcherOptions}
              filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          {contactLogin && (
            <Form.Item name="watch_contact" valuePropName="checked" initialValue={true}>
              <Checkbox>Add {contactLogin.LoginName} as a watcher</Checkbox>
            </Form.Item>
          )}
          </>)}
          </>
        )}
      </Form>
    </Modal>
  );
}
